<template>
  <div class="card card-custom">
    <div class="card-body container">
      <div class="card-body p-0">
        <div class="wizard wizard-3" data-wizard-state="step-first">
          <div class="card card-custom card-shadowless rounded-top-0">
            <div class="card-body p-0">
              <div class="row justify-content-center py-8 px-8 py-lg-10 px-lg-10">
                <div class="col-xl-12 col-xxl-12">
                  <div class="form mt-0">
                    <!--begin: Hub navigation-->
                    <div class="d-flex flex-wrap align-items-center mb-5">
                      <button
                        class="btn btn-sm btn-light-primary font-weight-bold text-uppercase mr-2 mb-2"
                        @click.prevent="goWorkQueue"
                      >
                        ← Work Queue
                      </button>
                      <button
                        v-if="reviewClientId"
                        class="btn btn-sm btn-light font-weight-bold text-uppercase mr-2 mb-2"
                        @click.prevent="goClient"
                      >
                        Open client
                      </button>
                      <button
                        v-if="reviewPolicyId"
                        class="btn btn-sm btn-light font-weight-bold text-uppercase mb-2"
                        @click.prevent="goPolicy"
                      >
                        Open policy
                      </button>
                    </div>
                    <!--end: Hub navigation-->

                    <b-alert v-if="redirectError" variant="warning" show>
                      <div class="text-dark">{{ redirectError }}</div>
                    </b-alert>
                    <billing-processing-panel
                      v-if="!redirecting"
                      :ledger-row-id="routeLedgerRowId"
                      :transaction-id="routeTransactionId"
                      @loading="onPanelLoading"
                      @review-loaded="onReviewLoaded"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@import "@/assets/sass/pages/wizard/wizard-3.scss";

.page-loading {
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>

<script>
import { ADD_BODY_CLASSNAME, REMOVE_BODY_CLASSNAME } from "@/core/services/store/htmlclass.module";
import { getBillingReview } from "@/services/billing-review-service";
import BillingProcessingPanel from "./BillingProcessingPanel.vue";

/**
 * Thin route shell for `#/billing-processing/:transactionId`.
 * When the id is a workbench ledger row and the review resolves a Zoho transaction,
 * redirect into the NB/RN wizard at step 7 (Processing and Finalisation) so operators
 * keep client/policy chrome. Otherwise embed BillingProcessingPanel for bookmarks/demos.
 */
export default {
  name: "BillingProcessing",

  components: {
    BillingProcessingPanel
  },

  data() {
    return {
      redirecting: false,
      redirectError: null,
      review: null
    };
  },

  computed: {
    routeParamId: function() {
      return this.$route.params.transactionId || null;
    },

    routeLedgerRowId: function() {
      const id = this.routeParamId;
      const match = typeof id === "string" && id.match(/^ledger-(\d{5,25})$/);
      return match ? match[1] : null;
    },

    routeTransactionId: function() {
      if (this.routeLedgerRowId) {
        return null;
      }
      const id = this.routeParamId;
      if (typeof id === "string" && id && !/^ledger-/.test(id)) {
        return id;
      }
      return null;
    },

    reviewClientId: function() {
      if (!this.review || !this.review.policy) return null;
      return this.review.policy.clientId || this.review.clientId || null;
    },

    reviewPolicyId: function() {
      if (!this.review || !this.review.policy) return null;
      return this.review.policy.id || this.review.policy.policyId || null;
    }
  },

  created() {
    // Hide the panel until redirect resolves for ledger bookmarks (avoid double-fetch flash).
    if (this.routeLedgerRowId || (this.$route.query && this.$route.query.transactionId)) {
      this.redirecting = true;
    }
  },

  mounted() {
    this.$store.commit("setWizardTitle", "Billing Processing");
    this.tryRedirectIntoWizard();
  },

  methods: {
    goWorkQueue: function() {
      this.$router.push({ path: "/workbench", query: { lane: "posting" } }).catch(() => {});
    },

    goClient: function() {
      if (this.reviewClientId) {
        this.$router.push(`/workbench/clients/${this.reviewClientId}`).catch(() => {});
      }
    },

    goPolicy: function() {
      if (this.reviewPolicyId) {
        this.$router.push(`/workbench/policies/${this.reviewPolicyId}`).catch(() => {});
      }
    },

    onPanelLoading: function(isLoading) {
      if (isLoading) {
        this.startPageLoading();
      } else {
        this.stopPageLoading();
      }
    },

    onReviewLoaded: function(review) {
      this.review = review;
    },

    tryRedirectIntoWizard: function() {
      // Prefer query override when an upstream already resolved wizard context.
      const q = this.$route.query || {};
      if (q.transactionId && q.pageNumber) {
        this.redirectToWizard(String(q.transactionId), this.routeLedgerRowId, q.brand);
        return;
      }

      if (!this.routeLedgerRowId) {
        // Bare txn / demo ids: keep embedding the panel (no wizard redirect without brand/txn certainty).
        return;
      }

      this.redirecting = true;
      this.startPageLoading();
      getBillingReview(`ledger-${this.routeLedgerRowId}`)
        .then(model => {
          this.review = model;
          if (model && model.resolutionError) {
            this.redirectError = model.resolutionError;
            this.redirecting = false;
            this.stopPageLoading();
            return;
          }
          const txnId = model && model.transactionId;
          if (!txnId) {
            this.redirecting = false;
            this.stopPageLoading();
            return;
          }
          const brand =
            (model.policy && model.policy.brand) ||
            q.brand ||
            "";
          this.redirectToWizard(String(txnId), this.routeLedgerRowId, brand);
        })
        .catch(() => {
          // Fall back to embedded panel so old bookmarks still work.
          this.redirecting = false;
          this.stopPageLoading();
        });
    },

    redirectToWizard: function(transactionId, ledgerRowId, brand) {
      const query = {
        transactionId: transactionId,
        pageNumber: "7"
      };
      if (ledgerRowId) {
        query.ledgerRowId = String(ledgerRowId);
      }
      if (brand) {
        query.brand = String(brand);
      }
      this.$router.replace({ path: "/wizard/nb-rn", query: query });
    },

    startPageLoading: function() {
      this.$store.dispatch(ADD_BODY_CLASSNAME, "page-loading");
    },

    stopPageLoading: function() {
      this.$store.dispatch(REMOVE_BODY_CLASSNAME, "page-loading");
    }
  }
};
</script>
